import { ConversationLabel, Label } from '../types/label';

export const LEAD_TEMPERATURE_CODES = [
  'lead-frio',
  'lead-morno',
  'lead-quente',
  'lead-estrategico',
  'fora-do-perfil',
] as const;

export type LeadTemperatureCode = (typeof LEAD_TEMPERATURE_CODES)[number];

export const LEAD_TEMPERATURE_DISPLAY_PRIORITY: readonly LeadTemperatureCode[] = [
  'lead-estrategico',
  'lead-quente',
  'lead-morno',
  'lead-frio',
  'fora-do-perfil',
];

export interface LeadTemperatureDisplay {
  code: LeadTemperatureCode;
  label: string;
  color?: string | null;
  appliedByBot?: boolean;
}

export const LEAD_TEMPERATURE_DEFAULT_DESCRIPTIONS: Record<LeadTemperatureCode, string> = {
  'lead-frio': 'Frio',
  'lead-morno': 'Morno',
  'lead-quente': 'Quente',
  'lead-estrategico': 'Estratégico',
  'fora-do-perfil': 'Fora do perfil',
};

export const LEAD_TEMPERATURE_DEFAULT_COLORS: Record<LeadTemperatureCode, string> = {
  'lead-frio': '#0284c7',
  'lead-morno': '#d97706',
  'lead-quente': '#dc2626',
  'lead-estrategico': '#7c3aed',
  'fora-do-perfil': '#6b7280',
};

export const LEAD_TEMPERATURE_TONE_CLASSES: Record<LeadTemperatureCode, string> = {
  'lead-quente': 'bg-red-100 text-red-800',
  'lead-morno': 'bg-amber-100 text-amber-800',
  'lead-estrategico': 'bg-violet-100 text-violet-800',
  'lead-frio': 'bg-sky-100 text-sky-800',
  'fora-do-perfil': 'bg-gray-200 text-gray-700',
};

export const isLeadTemperatureCode = (value: string): value is LeadTemperatureCode =>
  LEAD_TEMPERATURE_CODES.includes(value as LeadTemperatureCode);

export const resolveLeadTemperatureColor = (
  code: LeadTemperatureCode,
  color?: string | null,
): string => {
  const trimmedColor = color?.trim();
  if (trimmedColor)
  {
    return trimmedColor;
  }

  return LEAD_TEMPERATURE_DEFAULT_COLORS[code];
};

export const formatLeadTemperatureLabel = (
  code: LeadTemperatureCode,
  description?: string | null,
): string => {
  const trimmedDescription = description?.trim();
  if (trimmedDescription)
  {
    return trimmedDescription;
  }

  return LEAD_TEMPERATURE_DEFAULT_DESCRIPTIONS[code];
};

export const findLeadTemperatureLabel = (
  labels?: ReadonlyArray<Pick<Label, 'name' | 'color' | 'description'>>,
): LeadTemperatureDisplay | null => {
  if (!labels || labels.length === 0)
  {
    return null;
  }

  for (const code of LEAD_TEMPERATURE_DISPLAY_PRIORITY)
  {
    const match = labels.find((label) => label.name === code);
    if (match)
    {
      return {
        code,
        label: formatLeadTemperatureLabel(code, match.description),
        color: resolveLeadTemperatureColor(code, match.color),
      };
    }
  }

  return null;
};

export const findLeadTemperatureFromConversationLabels = (
  conversationLabels?: ReadonlyArray<
    Pick<ConversationLabel, 'labelName' | 'labelColor' | 'labelDescription' | 'appliedByBot'>
  >,
): LeadTemperatureDisplay | null => {
  if (!conversationLabels || conversationLabels.length === 0)
  {
    return null;
  }

  for (const code of LEAD_TEMPERATURE_DISPLAY_PRIORITY)
  {
    const match = conversationLabels.find((item) => item.labelName === code);
    if (match)
    {
      return {
        code,
        label: formatLeadTemperatureLabel(code, match.labelDescription),
        color: resolveLeadTemperatureColor(code, match.labelColor),
        appliedByBot: match.appliedByBot,
      };
    }
  }

  return null;
};

export const resolveLeadTemperatureDisplay = (
  conversationLabels?: ReadonlyArray<
    Pick<ConversationLabel, 'labelName' | 'labelColor' | 'labelDescription' | 'appliedByBot'>
  >,
  summaryLabels?: ReadonlyArray<Pick<Label, 'name' | 'color' | 'description'>>,
  canonicalCode?: string | null,
): LeadTemperatureDisplay | null =>
  findLeadTemperatureFromConversationLabels(conversationLabels)
  ?? findLeadTemperatureLabel(summaryLabels)
  ?? resolveLeadTemperatureFromCode(canonicalCode);

export const normalizeLeadTemperatureCode = (value?: string | null): LeadTemperatureCode | null => {
  if (!value || !isLeadTemperatureCode(value))
  {
    return null;
  }

  return value;
};

export const resolveLeadTemperatureFromCode = (
  value?: string | null,
  appliedByBot?: boolean,
): LeadTemperatureDisplay | null => {
  const code = normalizeLeadTemperatureCode(value);
  if (!code)
  {
    return null;
  }

  return {
    code,
    label: formatLeadTemperatureLabel(code),
    color: LEAD_TEMPERATURE_DEFAULT_COLORS[code],
    appliedByBot,
  };
};

export interface ConversationLabelDisplay {
  text: string;
  color: string;
}

export const getConversationLabelDisplay = (
  item: Pick<ConversationLabel, 'labelName' | 'labelColor' | 'labelDescription'>,
): ConversationLabelDisplay => {
  const trimmedDescription = item.labelDescription?.trim();
  return {
    text: trimmedDescription || item.labelName,
    color: item.labelColor?.trim() || '#2563eb',
  };
};
